/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link } from "react-router-dom";

import {
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
  Pagination,
  Stack,
} from "@mui/material";

import { Refresh } from "@mui/icons-material";
import ShopHeaderMini from "components/elements/main/ShopHeaderMini";
import MockProductList from "components/elements/main/MockProductList";
import UnifiedPullToRefresh from "components/elements/main/UnifiedPullToRefresh";
import * as Actions from "actions";
import {
  getLocalDate,
  isMarketPlace,
  getURI,
  getPriceCurrency,
} from "helpers/utils";
import { CURRENCY, DATE_FORMAT } from "config/constants";

const ShopOrderList = (props) => {
  const {
    isLoading,
    shoporderlist,
    currency,
    history,
    bootupInfo,
    getShopOrderlistProducts,
    match,
    pagination,
  } = props;

  const [currentPage, setCurrentPage] = useState(1);

  const shopId = isMarketPlace()
      ? match.params.shopId
      : bootupInfo.appState.subdomain_shopId;

  useEffect(() => {
    getShopOrderlistProducts(shopId, !isMarketPlace(), currentPage);
  }, [getShopOrderlistProducts, shopId, currentPage]);

  const handleRefresh = () => {
    getShopOrderlistProducts(shopId, !isMarketPlace(), currentPage);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderEmpty = () => (
      <Box textAlign="center" py={6}>
        <Typography variant="h6" color="text.secondary">
          No orders yet
        </Typography>
      </Box>
  );

  return (
      <UnifiedPullToRefresh
        onRefresh={handleRefresh}
        isRefreshing={isLoading}
        sx={{ py: 3 }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>

          <Box sx={{ mb: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <IconButton
              onClick={() => getShopOrderlistProducts(shopId, !isMarketPlace())}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : <Refresh />}
            </IconButton>
          </Box>

        {/* Loading */}
        {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <MockProductList />
            </Box>
        ) : shoporderlist.length === 0 ? (
            renderEmpty()
        ) : (
            <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 0 }}>
                {/* Table Header - Hidden on mobile */}
                <Grid 
                  container 
                  px={3} 
                  py={2} 
                  sx={{ 
                    background: "rgba(0, 0, 0, 0.02)",
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    display: { xs: 'none', md: 'flex' }
                  }}
                >
                  <Grid item xs={3}>
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      Order ID
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      Date
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      Status
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      Total
                    </Typography>
                  </Grid>
                </Grid>

                {/* Orders */}
                {shoporderlist.map((order, idx) => (
                    <Box
                        key={order._id}
                        onClick={() =>
                            history.push(
                                getURI(
                                    process.env.REACT_APP_TYPE,
                                    "shop_order",
                                    shopId,
                                    "",
                                    order._id
                                )
                            )
                        }
                        sx={{
                          "&:hover": { 
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                            '& .order-id': {
                              color: 'primary.dark'
                            }
                          },
                          cursor: "pointer",
                          transition: 'all 0.2s ease',
                          borderBottom: idx < shoporderlist.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
                        }}
                    >
                      {/* Desktop Layout */}
                      <Grid 
                        container 
                        px={3} 
                        py={2.5} 
                        alignItems="center"
                        sx={{ display: { xs: 'none', md: 'flex' } }}
                      >
                        <Grid item xs={3}>
                          <Typography 
                            variant="body1" 
                            className="order-id"
                            sx={{ 
                              color: 'primary.main',
                              fontWeight: 600,
                              transition: 'color 0.2s ease',
                              fontSize: '1rem'
                            }}
                          >
                            #{order.order_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                            {getLocalDate(order.created_at, DATE_FORMAT)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.75,
                              borderRadius: 1.5,
                              backgroundColor: 
                                order.status === "pending"
                                  ? "warning.lighter"
                                  : order.status === "paid"
                                    ? "success.lighter"
                                    : "action.hover",
                            }}
                          >
                            <Typography
                                variant="body1"
                                sx={{
                                  color:
                                      order.status === "pending"
                                          ? "warning.main"
                                          : order.status === "paid"
                                              ? "success.main"
                                              : "text.secondary",
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                  fontSize: '0.95rem'
                                }}
                            >
                              {order.status}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ fontSize: '1rem' }}>
                            {getPriceCurrency(order.total_order, currency)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Mobile Layout - Card Style */}
                      <Box 
                        sx={{ 
                          display: { xs: 'block', md: 'none' },
                          px: 2,
                          py: 2
                        }}
                      >
                        <Grid container spacing={1.5}>
                          {/* Row 1: Order ID and Status */}
                          <Grid item xs={6}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Order ID
                            </Typography>
                            <Typography 
                              variant="body2" 
                              className="order-id"
                              sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                transition: 'color 0.2s ease'
                              }}
                            >
                              #{order.order_id}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Status
                            </Typography>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: 
                                  order.status === "pending"
                                    ? "warning.lighter"
                                    : order.status === "paid"
                                      ? "success.lighter"
                                      : "action.hover",
                              }}
                            >
                              <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                        order.status === "pending"
                                            ? "warning.main"
                                            : order.status === "paid"
                                                ? "success.main"
                                                : "text.secondary",
                                    fontWeight: 600,
                                    textTransform: 'capitalize'
                                  }}
                              >
                                {order.status}
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Row 2: Date and Total */}
                          <Grid item xs={6}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Date
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              {getLocalDate(order.created_at, DATE_FORMAT)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'right' }}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Total
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="text.primary">
                              {getPriceCurrency(order.total_order, currency)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                ))}
              </CardContent>
            </Card>
        )}

        {/* Pagination */}
        {!isLoading && shoporderlist.length > 0 && pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <Stack spacing={2}>
                    <Pagination 
                        count={pagination.totalPages} 
                        page={currentPage} 
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                    />
                    <Typography variant="body2" color="text.secondary" align="center">
                        Showing {((currentPage - 1) * pagination.limit) + 1} - {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} orders
                    </Typography>
                </Stack>
            </Box>
        )}
        </Container>
      </UnifiedPullToRefresh>
  );
};

ShopOrderList.defaultProps = {
  isLoading: false,
  currency: CURRENCY,
};

ShopOrderList.propTypes = {
  isLoading: PropTypes.bool,
  shoporderlist: PropTypes.arrayOf(PropTypes.object).isRequired,
  currency: PropTypes.string,
  history: PropTypes.object.isRequired,
  bootupInfo: PropTypes.object.isRequired,
  getShopOrderlistProducts: PropTypes.func.isRequired,
  pagination: PropTypes.object,
};

const shopOrderList = connect(
    ({ userorders, bootupInfo }) => ({
      bootupInfo,
      isLoading: userorders.isLoading,
      shoporderlist: userorders.shoporderlist,
      currency: bootupInfo.symbol,
      pagination: userorders.pagination,
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(ShopOrderList);

export { shopOrderList as default };

